import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import './Modal.css';
import { TransactionType } from '../../types/restaurants';

interface CreateRestaurantTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (transactionType: TransactionType, amount: number, description: string) => Promise<void>;
  restaurantName: string;
}

const CreateRestaurantTransactionModal: React.FC<CreateRestaurantTransactionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  restaurantName
}) => {
  const { t } = useTranslation();
  const [transactionType, setTransactionType] = useState<TransactionType>(TransactionType.FILL);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuickTextClick = () => {
    setDescription(t('modals.createRestaurantTransaction.quickText'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amountNum = parseInt(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert(t('modals.createRestaurantTransaction.validationAmount'));
      return;
    }

    if (!description.trim()) {
      alert(t('modals.createRestaurantTransaction.validationDescription'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(transactionType, amountNum, `<p>${description}</p>`);
      // Reset form
      setAmount('');
      setDescription('');
      setTransactionType(TransactionType.FILL);
      onClose();
    } catch (error) {
      console.error('Failed to create transaction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAmount('');
      setDescription('');
      setTransactionType(TransactionType.FILL);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('modals.createRestaurantTransaction.title')}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label>{t('modals.createRestaurantTransaction.transactionType')}</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <label
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${transactionType === TransactionType.FILL ? '#10b981' : '#ddd'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                backgroundColor: transactionType === TransactionType.FILL ? '#ecfdf5' : 'white',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                value={TransactionType.FILL}
                checked={transactionType === TransactionType.FILL}
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
                style={{ marginRight: '8px' }}
              />
              {t('modals.createRestaurantTransaction.fill')}
            </label>
            <label
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${transactionType === TransactionType.WITHDRAW ? '#ef4444' : '#ddd'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                backgroundColor: transactionType === TransactionType.WITHDRAW ? '#fef2f2' : 'white',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                value={TransactionType.WITHDRAW}
                checked={transactionType === TransactionType.WITHDRAW}
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
                style={{ marginRight: '8px' }}
              />
              {t('modals.createRestaurantTransaction.withdraw')}
            </label>
          </div>
        </div>

        <div className="modal-form-group">
          <label htmlFor="amount">{t('modals.createRestaurantTransaction.amount')}</label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('modals.createRestaurantTransaction.amountPlaceholder')}
            required
            min="1"
            disabled={isSubmitting}
          />
          <small style={{ color: '#6b7280', fontSize: '13px' }}>
            {t('modals.createRestaurantTransaction.amountHint')}
          </small>
        </div>

        <div className="modal-form-group">
          <label htmlFor="description">{t('modals.createRestaurantTransaction.description')}</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('modals.createRestaurantTransaction.descriptionPlaceholder')}
            rows={4}
            required
            disabled={isSubmitting}
            style={{ resize: 'vertical' }}
          />
          <button
            type="button"
            onClick={handleQuickTextClick}
            disabled={isSubmitting}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              fontSize: '13px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('modals.createRestaurantTransaction.quickText')}
          </button>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="action-button accept"
            style={{ backgroundColor: '#6b7280' }}
          >
            {t('modals.createRestaurantTransaction.cancel')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="action-button accept"
            style={{ backgroundColor: '#3b82f6' }}
          >
            {isSubmitting
              ? t('modals.createRestaurantTransaction.creating')
              : t('modals.createRestaurantTransaction.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateRestaurantTransactionModal;
